import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar, Compass, BookOpen, Shield, ArrowRight, Star, Sun, Moon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
    const { t, language } = useLanguage();
    const { isAuthenticated } = useAuth();
    const [todayData, setTodayData] = useState(null);

    useEffect(() => {
        const fetchTodayData = async () => {
            try {
                const today = new Date();
                const year = today.getFullYear();
                const month = today.getMonth() + 1;
                const day = today.getDate();
                
                // Fetch monthly data which has dong_gong ratings
                const res = await fetch(`${API_URL}/api/tong-shu/monthly?year=${year}&month=${month}`);
                if (res.ok) {
                    const data = await res.json();
                    // Find today's data in the monthly response
                    const todayEntry = data.days?.find(d => d.day === day);
                    if (todayEntry) {
                        setTodayData(todayEntry);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch today data:', err);
            }
        };
        fetchTodayData();
    }, []);

    // Format today's date
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    });

    // Determine if it's day or night (6am-6pm is day)
    const currentHour = today.getHours();
    const isDayTime = currentHour >= 6 && currentHour < 18;

    const features = [
        {
            icon: <Calendar className="w-8 h-8" />,
            title: t('accurateCalculations'),
            titleZh: '精准计算',
            desc: t('accurateDesc'),
            descZh: '基于节气和传统经典规则'
        },
        {
            icon: <Compass className="w-8 h-8" />,
            title: t('comprehensiveData'),
            titleZh: '全面数据',
            desc: t('comprehensiveDesc'),
            descZh: '建除十二神、二十八宿、纳音等'
        },
        {
            icon: <BookOpen className="w-8 h-8" />,
            title: t('educationalMode'),
            titleZh: '教学模式',
            desc: t('educationalDesc'),
            descZh: '初学者和专业模式切换'
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: 'Practitioner Validated',
            titleZh: '专业验证',
            desc: 'Built for real consultations and teaching',
            descZh: '适用于实际咨询和教学'
        }
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-wealth/5" />
                <div className="absolute top-20 right-10 w-72 h-72 bg-wealth/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
                
                <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 lg:py-40">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 animate-fade-in">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-wealth/10 text-wealth rounded-full text-sm font-medium">
                                <Star className="w-4 h-4" />
                                <span>Professional Grade Tool</span>
                            </div>
                            
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
                                {language === 'zh' ? (
                                    <>
                                        <span className="font-chinese">专业</span>
                                        <span className="text-wealth font-chinese">通书</span>
                                        <span className="font-chinese">排盘</span>
                                    </>
                                ) : (
                                    <>
                                        Professional <span className="text-wealth">Tong Shu</span> Plotter
                                    </>
                                )}
                            </h1>
                            
                            <p className="text-lg text-muted-foreground max-w-xl">
                                {language === 'zh' 
                                    ? '基于节气的专业级中国日历计算系统，适用于择日、风水咨询及教学'
                                    : 'Practitioner-grade Chinese almanac calculations using the Solar Calendar (节气) for date selection, Feng Shui consultation, and teaching.'
                                }
                            </p>
                            
                            <div className="flex flex-wrap gap-4">
                                <Link to={isAuthenticated ? '/calendar' : '/register'}>
                                    <Button size="lg" className="rounded-full gap-2 bg-primary hover:bg-primary/90" data-testid="get-started-btn">
                                        {t('getStarted')}
                                        <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </Link>
                                <Link to="#features">
                                    <Button variant="outline" size="lg" className="rounded-full" data-testid="learn-more-btn">
                                        {t('learnMore')}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        
                        {/* Hero Visual */}
                        <div className="relative animate-fade-in stagger-2">
                            <div className="relative aspect-square max-w-md mx-auto">
                                {/* Decorative compass */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-72 h-72 rounded-full border-4 border-muted/50 relative">
                                        <div className="absolute inset-4 rounded-full border-2 border-muted/30" />
                                        <div className="absolute inset-8 rounded-full bg-card shadow-lg flex items-center justify-center">
                                            <div className="text-center space-y-1 px-4">
                                                <div className="font-bold text-2xl text-foreground">{formattedDate}</div>
                                                {todayData && (
                                                    <>
                                                        <div className="text-xs text-muted-foreground mt-2">
                                                            {todayData.pillars?.day_chinese || ''} {todayData.pillars?.day_animal || ''}
                                                        </div>
                                                        {todayData.dong_gong && (
                                                            <div className="flex justify-center mt-2">
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                    todayData.dong_gong.rating === 'auspicious' ? 'bg-emerald-100 text-emerald-700' :
                                                                    todayData.dong_gong.rating === 'neutral' ? 'bg-amber-100 text-amber-700' :
                                                                    'bg-red-100 text-red-700'
                                                                }`}>
                                                                    {todayData.dong_gong.chinese} {todayData.dong_gong.english}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-wealth font-medium mt-1">
                                                            {todayData.day_officer?.chinese || ''} {todayData.day_officer?.english || ''}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {/* Direction markers */}
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 font-chinese text-sm text-muted-foreground">北</div>
                                        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 font-chinese text-sm text-muted-foreground">南</div>
                                        <div className="absolute top-1/2 -left-3 -translate-y-1/2 font-chinese text-sm text-muted-foreground">西</div>
                                        <div className="absolute top-1/2 -right-3 -translate-y-1/2 font-chinese text-sm text-muted-foreground">东</div>
                                    </div>
                                </div>
                                
                                {/* Floating elements */}
                                <div className={`absolute top-4 right-4 p-3 bg-card rounded-xl shadow-lg ${isDayTime ? 'animate-pulse-glow' : ''}`}>
                                    <Sun className={`w-6 h-6 ${isDayTime ? 'text-wealth' : 'text-muted-foreground'}`} />
                                </div>
                                <div className={`absolute bottom-4 left-4 p-3 bg-card rounded-xl shadow-lg ${!isDayTime ? 'animate-pulse-glow' : ''}`}>
                                    <Moon className={`w-6 h-6 ${!isDayTime ? 'text-primary' : 'text-muted-foreground'}`} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 bg-muted/30">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16 animate-fade-in">
                        <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
                            {language === 'zh' ? '功能特点' : 'Key Features'}
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            {language === 'zh' 
                                ? '专为风水顾问、八字学生和专业人士设计'
                                : 'Designed for Feng Shui consultants, BaZi students, and serious practitioners'
                            }
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="group p-6 bg-card rounded-2xl border border-border hover:border-wealth/50 hover:shadow-xl transition-all duration-300 animate-fade-in"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="p-3 bg-wealth/10 text-wealth rounded-xl w-fit mb-4 group-hover:bg-wealth group-hover:text-white transition-colors">
                                    {feature.icon}
                                </div>
                                <h3 className="font-serif text-lg font-semibold mb-2">
                                    {language === 'zh' ? feature.titleZh : feature.title}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    {language === 'zh' ? feature.descZh : feature.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Core Systems Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6 animate-fade-in">
                            <h2 className="font-serif text-3xl sm:text-4xl font-bold">
                                {language === 'zh' ? '核心计算系统' : 'Core Calculation Systems'}
                            </h2>
                            <div className="space-y-4">
                                {[
                                    { zh: '建除十二神 (12 Day Officers)', en: '12 Day Officers (建除十二神)', desc: 'Establish, Remove, Full, Balance, Stable, Hold, Break, Danger, Success, Receive, Open, Close' },
                                    { zh: '二十八宿 (28 Lunar Mansions)', en: '28 Lunar Mansions (二十八宿)', desc: 'Daily constellation influences with suitable and unsuitable activities' },
                                    { zh: '纳音五行 (Na Yin Elements)', en: 'Na Yin Elements (纳音)', desc: '60 Jia Zi cycle with full elemental associations' },
                                    { zh: '方位吉凶 (Directional Influences)', en: 'Directional Influences (方位)', desc: 'Happiness, Wealth, Conflict, and Five Ghost directions' },
                                    { zh: '冲煞提醒 (Clash Warnings)', en: 'Clash Warnings (冲煞)', desc: 'Direct clash, day breaker, harm, and punishment indicators' },
                                ].map((item, index) => (
                                    <div key={index} className="flex gap-4 p-4 bg-muted/50 rounded-xl">
                                        <div className="w-2 h-2 mt-2 rounded-full bg-wealth flex-shrink-0" />
                                        <div>
                                            <div className="font-medium">
                                                {language === 'zh' ? item.zh : item.en}
                                            </div>
                                            <div className="text-sm text-muted-foreground">{item.desc}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="relative animate-fade-in stagger-3">
                            <div className="bg-card rounded-2xl border shadow-xl p-6 space-y-4">
                                <div className="flex justify-between items-center border-b pb-4">
                                    <span className="text-sm text-muted-foreground">Sample Output</span>
                                    <span className="font-chinese text-xl font-bold">2026年1月15日</span>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-4">
                                    {['年柱', '月柱', '日柱'].map((pillar, i) => (
                                        <div key={i} className="pillar-box text-center">
                                            <div className="text-xs text-muted-foreground mb-1">{pillar}</div>
                                            <div className="font-chinese text-xl font-bold text-primary">
                                                {i === 0 ? '乙巳' : i === 1 ? '己丑' : '甲子'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="flex flex-wrap gap-2">
                                    <span className="officer-badge auspicious">建 Establish</span>
                                    <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-sm">黄道</span>
                                    <span className="px-3 py-1 bg-wealth/20 text-wealth rounded-full text-sm">吉</span>
                                </div>

                                {/* Energy Chart Sample */}
                                <div className="border-t pt-4 mt-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-sm font-medium">{language === 'zh' ? '月度能量图' : 'Monthly Energy Chart'}</span>
                                        <span className="text-xs text-muted-foreground">🐍 Snake</span>
                                    </div>
                                    <div className="flex items-end gap-1 h-24 bg-muted/30 rounded-lg p-2">
                                        {[65, 82, 45, 78, 30, 90, 55, 72, 40, 85, 60, 75].map((value, i) => (
                                            <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full">
                                                <div className="flex-1 w-full flex items-end">
                                                    <div 
                                                        className={`w-full rounded-t transition-all ${
                                                            value >= 80 ? 'bg-emerald-500' : 
                                                            value >= 50 ? 'bg-amber-500' : 
                                                            'bg-red-400'
                                                        }`}
                                                        style={{ height: `${value}%` }}
                                                    />
                                                </div>
                                                <span className="text-[9px] text-muted-foreground font-medium">{i + 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-center gap-4 mt-3 text-[10px]">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> High</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Medium</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Low</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-primary text-primary-foreground">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6">
                        {language === 'zh' ? '开始使用专业通书系统' : 'Start Using Professional Tong Shu'}
                    </h2>
                    <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                        {language === 'zh' 
                            ? '免费创建账号，立即使用完整的通书排盘功能'
                            : 'Create a free account and access full Tong Shu calculation features instantly'
                        }
                    </p>
                    <Link to={isAuthenticated ? '/calendar' : '/register'}>
                        <Button 
                            size="lg" 
                            variant="secondary" 
                            className="rounded-full gap-2"
                            data-testid="cta-get-started-btn"
                        >
                            {isAuthenticated ? t('dashboard') : t('getStarted')}
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Landing;
